import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, LayoutGrid, GraduationCap, BrainCircuit, Briefcase, Cloud, Code2, FolderKanban, HeartPulse, LineChart, Megaphone, Network, ShieldCheck, BarChart3, Coins, ClipboardList } from "lucide-react";

const LUCIDE_MAP = { GraduationCap, BrainCircuit, Briefcase, Cloud, Code2, FolderKanban, HeartPulse, LineChart, Megaphone, Network, ShieldCheck, BarChart3, Coins, ClipboardList };
import api from "@/lib/api";
import CourseCard from "@/components/CourseCard";

const CAT_ICONS = {
  "data-science-ai": "BarChart3", "technology": "Code2", "business-management": "Briefcase",
  "digital-marketing": "Megaphone", "cloud-devops": "Cloud", "cyber-security": "ShieldCheck",
  "networking": "Network", "finance": "Coins", "health": "HeartPulse", "project-management": "ClipboardList",
};

export default function TopPrograms({ data }) {
  const [section, setSection] = useState(data);
  const [programs, setPrograms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("");
  const finderRef = useRef(null);

  useEffect(() => {
    if (data !== undefined) { setSection(data); return; }
    api.get("/website/sections/featured_programs").then((r) => setSection(r.data)).catch(() => setSection(null));
  }, [data]);

  useEffect(() => {
    api.get("/programs").then((r) => setPrograms(r.data.programs || [])).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data.categories || [])).catch(() => {});
  }, []);

  const settings = section?.settings || {};
  const cfg = section?.items || [];
  const maxCards = Number(settings.max_cards) || 6;

  const cats = useMemo(() => {
    const orderOf = (slug) => { const i = cfg.findIndex((x) => x.slug === slug); return i === -1 ? 999 : i; };
    return categories
      .map((c) => {
        const o = cfg.find((x) => x.slug === c.slug) || {};
        return { ...c, label: o.label || c.name, icon: o.icon || "", visible: o.visible !== false };
      })
      .filter((c) => c.visible)
      .sort((a, b) => orderOf(a.slug) - orderOf(b.slug));
  }, [categories, cfg]);

  const countFor = (slug) => programs.filter((p) => p.category_slug === slug).length;
  const list = (active ? programs.filter((p) => p.category_slug === active) : programs).slice(0, maxCards);
  const visibleCats = cats.filter((c) => countFor(c.slug) > 0);

  if (!section || section.active === false) return null;

  const CatIcon = ({ name, className }) => {
    const I = LUCIDE_MAP[name] || LayoutGrid;
    return <I className={className} />;
  };

  return (
    <section className="bg-white border-b border-slate-200" data-testid="top-programs">
      <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-12">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#F5B301]" data-testid="top-programs-eyebrow">{settings.eyebrow || "Explore Programs"}</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-[#17263B] mt-2" data-testid="top-programs-title">
              {settings.title_black || "Top"} <span className="text-[#F5B301]">{settings.title_accent || "IT Programs"}</span>
            </h2>
            {settings.subtitle && <p className="text-sm sm:text-base text-slate-500 mt-2 max-w-2xl" data-testid="top-programs-subtitle">{settings.subtitle}</p>}
          </div>
          <Link to={settings.view_all_link || "/courses"} className="flex items-center gap-1.5 text-sm font-bold text-slate-900 hover:text-[#d9a400]" data-testid="top-programs-view-all">
            {settings.view_all_label || "View all programs"} <ArrowRight className="w-4 h-4 text-[#F5B301]" />
          </Link>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 mt-8">
          <aside className="lg:w-64 xl:w-72 shrink-0 rounded-2xl lg:bg-[#10141C] lg:p-5 lg:sticky lg:top-24 self-start w-full" data-testid="program-finder">
            <p className="hidden lg:block text-white font-extrabold text-base">Program Finder</p>
            <p className="hidden lg:block text-slate-400 text-xs mt-1 mb-4">Choose a category to explore our top programs</p>
            <div className="lg:hidden flex items-center gap-2" data-testid="finder-mobile-strip">
              <button onClick={() => finderRef.current?.scrollBy({ left: -220, behavior: "smooth" })} aria-label="Scroll categories left"
                className="shrink-0 w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center" data-testid="finder-prev">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div ref={finderRef} className="flex gap-2 overflow-x-auto flex-1 py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <button onClick={() => setActive("")} data-testid="finder-all-mobile"
                  className={`shrink-0 rounded-full px-5 py-2.5 text-sm font-bold whitespace-nowrap transition-colors ${!active ? "bg-[#FFC72C] text-[#17263B]" : "bg-[#F5EFE6] text-[#17263B]"}`}>
                  All Programs
                </button>
                {visibleCats.map((c) => (
                  <button key={c.slug} onClick={() => setActive(c.slug)} data-testid={`finder-mobile-${c.slug}`}
                    className={`shrink-0 rounded-full px-5 py-2.5 text-sm font-bold whitespace-nowrap transition-colors ${active === c.slug ? "bg-[#FFC72C] text-[#17263B]" : "bg-[#F5EFE6] text-[#17263B]"}`}>
                    {c.label}
                  </button>
                ))}
              </div>
              <button onClick={() => finderRef.current?.scrollBy({ left: 220, behavior: "smooth" })} aria-label="Scroll categories right"
                className="shrink-0 w-8 h-8 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center" data-testid="finder-next">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            <div className="hidden lg:flex lg:flex-col">
              <button onClick={() => setActive("")} data-testid="finder-all"
                className={`flex items-center gap-3 rounded-xl px-3.5 py-3.5 mb-2 text-left transition-colors ${!active ? "bg-[#FFC72C] text-[#17263B]" : "text-white hover:bg-white/10"}`}>
                <span className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${!active ? "bg-[#17263B]/10" : "bg-white/10"}`}>
                  <LayoutGrid className="w-5 h-5" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-bold truncate">All Programs</span>
                  <span className={`block text-[11px] ${!active ? "text-[#17263B]/70" : "text-slate-400"}`}>{programs.length} Programs</span>
                </span>
                <ChevronRight className="w-4 h-4 shrink-0 opacity-70" />
              </button>
              {visibleCats.map((c, ci) => (
                <button key={c.slug} onClick={() => setActive(c.slug)} data-testid={`finder-${c.slug}`}
                  className={`flex items-center gap-3 px-2 py-3.5 text-left transition-colors border-t border-white/10 ${active === c.slug ? "text-[#FFC72C]" : "text-white hover:bg-white/5 rounded-xl"}`}>
                  <span className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${active === c.slug ? "bg-[#FFC72C]/15" : "bg-white/10"}`}>
                    <CatIcon name={c.icon || CAT_ICONS[c.slug]} className="w-5 h-5" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-bold leading-tight">{c.label}</span>
                    <span className={`block text-[11px] mt-0.5 ${active === c.slug ? "text-[#FFC72C]/70" : "text-slate-400"}`}>{countFor(c.slug)} Programs</span>
                  </span>
                  <ChevronRight className="w-4 h-4 shrink-0 opacity-50" />
                </button>
              ))}
            </div>
          </aside>

          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="top-programs-grid">
            {list.map((p) => <CourseCard key={p.slug} program={p} />)}
            {list.length === 0 && <p className="text-sm text-slate-400 col-span-full py-10 text-center" data-testid="top-programs-empty">No programs in this category yet.</p>}
          </div>
        </div>
      </div>
    </section>
  );
}
